import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Navbar } from './common/Catalyst/navbar';
import AppSidebar from './AppSidebar';
import { Heading, Subheading } from './common/Catalyst/heading';
import { Divider } from './common/Catalyst/divider';
import { Button } from './common/Catalyst/button';
import LoadingSpinner from './LoadingSpinner';
import ShowAlert from './ShowAlert';
import AccountingTabs, {
  AccountingTab,
} from './accounting/AccountingTabs';
import { HoaReturn } from './clients/rust-api-client';
import { hoaService } from './clients/ApiClients';
import { useSyncedHoa } from './sync/hooks';
import { billingErrorMessage } from './utils/billingHelpers';
import { getUserTypeFromToken } from './utils/TokenUtils';
import {
  canManageChart,
  canPostBankActivity,
  canReconcile,
  canViewLedger,
} from './constants/UserTypes';
import BudgetGrid from './accounting/BudgetGrid';
import ChartOfAccounts from './accounting/ChartOfAccounts';
import MonthEndClose from './accounting/MonthEndClose';
import ReconciliationList from './accounting/ReconciliationList';
import ReportsPanel from './accounting/ReportsPanel';
import ReconciliationWorkspace from './accounting/ReconciliationWorkspace';

/**
 * Which screen of the HOA accounting area to show.
 *
 * The general ledger is a **third axis**, not a tab inside billing.
 * Billing is per-HOA policy and process — dues, runs, payments,
 * collections — and the per-home account lives on `HomeBillingPage`.
 * This is the association's own books: `hoa_id` x `fund_id` x
 * `account_id`. Stacking it into billing's strip would put eleven tabs
 * on one screen and imply a containment the two-ledgers decision in
 * `back-end-rs/docs/GL_DESIGN.md` explicitly denies.
 */
export type AccountingSection =
  | 'close'
  | 'reconciliations'
  | 'reports'
  | 'chart'
  | 'budgets';

interface Props {
  section: AccountingSection;
}

const AccountingPage: React.FC<Props> = ({ section }) => {
  const { hoaId, recId } = useParams<{ hoaId: string; recId?: string }>();
  const navigate = useNavigate();
  const userType = useMemo(() => getUserTypeFromToken(), []);

  const [fetchedHoa, setHoa] = useState<HoaReturn | null>(null);
  // The sync engine's copy when it has this association (#561); fetched otherwise.
  const syncedHoa = useSyncedHoa(hoaId);
  const haveSynced = !!syncedHoa;
  const hoa = syncedHoa ?? fetchedHoa;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHoa = async () => {
      if (!hoaId) return;
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const { data } = await hoaService.getHoa({ id: hoaId });
        setHoa(data);
      } catch (err) {
        console.error('Error fetching HOA:', err);
        setError(billingErrorMessage(err, "Couldn't load this association."));
      } finally {
        setLoading(false);
      }
    };
    void fetchHoa();
  }, [hoaId, haveSynced]);

  const base = `/hoa/${hoaId}/accounting`;
  const tabs: AccountingTab[] = [
    { to: base, label: 'Month-end close' },
    {
      to: `${base}/reconciliations`,
      label: 'Reconciliations',
      matches: [`${base}/reconciliations/`],
    },
    { to: `${base}/reports`, label: 'Reports' },
    // The chart and the budgets are Owner/Admin to write and everyone
    // to read, so the tabs are shown to anyone who can read the ledger;
    // what goes read-only for a Manager or a BoardMember is the editing
    // inside them.
    { to: `${base}/chart`, label: 'Chart of accounts' },
    { to: `${base}/budgets`, label: 'Budgets' },
  ];

  const body = (): React.ReactNode => {
    switch (section) {
      case 'reconciliations':
        if (!hoaId) return null;
        // One route tree: the workspace is the same tab as the list, so
        // the strip stays lit and browser back works.
        return recId ? (
          <ReconciliationWorkspace
            hoaId={hoaId}
            reconciliationId={recId}
            canReconcile={canReconcile(userType)}
            canPostBankActivity={canPostBankActivity(userType)}
          />
        ) : (
          <ReconciliationList
            hoaId={hoaId}
            canReconcile={canReconcile(userType)}
          />
        );
      case 'reports':
        return hoaId ? <ReportsPanel hoaId={hoaId} /> : null;
      case 'chart':
        return hoaId ? (
          <ChartOfAccounts
            hoaId={hoaId}
            canManage={canManageChart(userType)}
          />
        ) : null;
      case 'budgets':
        return hoaId ? (
          <BudgetGrid
            hoaId={hoaId}
            canManage={canManageChart(userType)}
          />
        ) : null;
      case 'close':
      default:
        return hoaId ? (
          <MonthEndClose
            hoaId={hoaId}
            canReconcile={canReconcile(userType)}
            canManageChart={canManageChart(userType)}
          />
        ) : null;
    }
  };

  // Reading is BoardMember and above. The two things this area exists
  // for — matching and closing — are gated further in, on the screens
  // that do them.
  if (!canViewLedger(userType)) {
    return (
      <SidebarLayout
        sidebar={<AppSidebar currentPage="hoas" />}
        navbar={<Navbar>{/* Your navbar content */}</Navbar>}
      >
        <Heading level={2}>Accounting</Heading>
        <Subheading className="mt-2">
          You do not have access to this association's books.
        </Subheading>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="hoas" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="flex justify-between items-center">
        <div>
          <Heading level={2}>
            {hoa ? `${hoa.name} — Accounting` : 'Accounting'}
          </Heading>
          <Subheading className="mt-1">
            The association's own books: reconcile, close, report.
          </Subheading>
        </div>
        {hoaId && (
          <Button plain onClick={() => navigate(`/hoa/${hoaId}`)}>
            Back to the association
          </Button>
        )}
      </div>

      <Divider className="mt-4 mb-6" />

      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load this association"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline={true}
          />
        </div>
      )}

      <AccountingTabs tabs={tabs} />

      {loading ? <LoadingSpinner /> : body()}
    </SidebarLayout>
  );
};

export default AccountingPage;
